import { useDebounce } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { isEmailInput, normalizeEmailInputValue } from './emailInput'

const variants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  registration: Partial<UseFormRegister<any>>
  containerClassName?: string
  labelClassName?: string
  label: string
  showLabel?: boolean
  errors?: FieldError
  info?: string
  size?: 'sm' | 'lg'
  loading?: boolean
  disabled?: boolean
  requiredAsterisk?: boolean
  delay?: number
}

export const InputSMDebounced = React.forwardRef<HTMLInputElement, React.PropsWithChildren<InputProps>>((props: InputProps, ref) => {
  const {
    className,
    containerClassName,
    labelClassName,
    registration,
    type = 'text',
    placeholder,
    autoComplete = 'off',
    id,
    showLabel = false,
    label,
    errors,
    info,
    loading = false,
    disabled = false,
    children,
    requiredAsterisk
  } = props
  const registrationProps = registration as React.InputHTMLAttributes<HTMLInputElement>
  const shouldNormalizeEmail = isEmailInput({ type, id, name: registrationProps.name, label })

  const [value, setValue] = useState<React.ChangeEvent<HTMLElement>>()
  const debouncedParams = useDebounce(value, props?.delay || 600)

  useEffect(() => {
    const onChange = (registration as any).onChange
    if (debouncedParams) {
      onChange(debouncedParams)
    }
  }, [debouncedParams])

  return (
    <AnimatePresence mode='wait'>
      {loading && (
        <motion.div
          key={`${id}-input-sm-skeleton`}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className={classNames('mb-2 animate-pulse', containerClassName)}
        >
          <div className={classNames('block ml-2 mb-2 h-[16px] bg-skeleton max-w-[20%] rounded-lg', { 'sr-only': !showLabel })} />
          <div className='relative'>
            <div className={classNames('px-4 py-2 rounded-3xl bg-skeleton w-full h-[36px]', className)} />
          </div>
          <p className='pl-2 pt-2 h-[5px]' />
        </motion.div>
      )}
      {!loading && (
        <motion.div
          key={`${id}-input-sm`}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className={classNames('mb-2', containerClassName)}
        >
          <label
            className={classNames('block pl-2 pb-2 text-sm text-text font-bold', labelClassName, { 'sr-only': !showLabel })}
            htmlFor={id}
          >
            {label} {requiredAsterisk && <span className='relative left-[-2px] text-error'>*</span>}
          </label>
          <div className='relative'>
            <input
              id={id}
              ref={ref}
              type={type}
              placeholder={placeholder}
              className={classNames(
                'input-bordered border border-border disabled:border-border bg-surface disabled:bg-surface-2 disabled:text-text-muted/70 placeholder:text-text-muted/50 text-text input w-full text-sm  h-[35px] sm:h-[40px] rounded-[20px] focus:outline-none',
                { 'input-error': errors },
                className
              )}
              disabled={disabled}
              autoComplete={autoComplete}
              {...registration}
              autoCapitalize={shouldNormalizeEmail ? 'none' : props.autoCapitalize}
              spellCheck={shouldNormalizeEmail ? false : props.spellCheck}
              onChange={(e) => {
                e.target.value = normalizeEmailInputValue(e.target.value, shouldNormalizeEmail) as string
                setValue(e)
              }}
            />
            {children}
          </div>
          <p
            className={classNames(
              'pl-2 pt-2 text-[11px]',
              info && !errors?.message && 'text-text-muted/70',
              (!info || errors?.message) && 'text-error'
            )}
          >
            {errors?.message || info}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

InputSMDebounced.displayName = 'InputSMDebounced'
